import { watch, set, getCurrentInstance } from 'vue';
import { useToast } from '@/composables/common/useToast';

/**
 * Reactive watchers for useEventForm.js. Encapsulates the 9 watchers that
 * cross-link localData fields (entrada compartida → eventos select,
 * preventa toggle → side effects, cesión temporal → bloqueos).
 *
 * Extracted from useEventForm.js to keep each composable under the canonical
 * ~200 LOC ceiling.
 */
export function useEventFormWatchers(localData, {
    getEventos,
    syncCesionTemporalAsiento,
    textoCondicionesLegalesPreventaDefault,
    metaTitleUserModified,
    cesionTemporalBloqueadaPor
}) {
    // Toast pattern: useToast() preferred, fallback to instance proxy (matches
    // useAbonadoCards.js / useFormEmail.js / useConfiguracionDeportistas.js).
    const toast = useToast();
    const instance = getCurrentInstance();
    const notify = (method, message) => {
        if (toast && toast[method]) {
            toast[method](message);
        } else if (instance?.proxy?.$toast) {
            instance.proxy.$toast[method](message);
        }
    };

    // Auto-mirror `meta_title` from `name` (titulo) + `author` (subtitulo)
    // from EventoBasicInfo, joined with " - " until the user manually edits
    // meta_title or the EDIT flow shipped a value from the API. Truncates to
    // 80 chars to honour the `max:80` rule on the meta_title input and stay
    // within `:maxlength="80"`. Either field can be empty; we drop falsy
    // values and the separator only when both are present. When both are
    // empty the meta_title is cleared.
    watch(
        [() => localData.name, () => localData.author],
        ([newName, newAuthor]) => {
            // Si el usuario editó meta_title a mano, su valor es la fuente de
            // verdad: el auto-mirror NO debe pisarlo. Antes este watcher reseteaba
            // el flag a false en cada cambio de name/author, así que editar el
            // título o el subtítulo borraba el SEO introducido manualmente y
            // `markMetaTitleAsUserModified()` no protegía nada.
            if (metaTitleUserModified.value) {
                return;
            }

            const parts = [newName, newAuthor].filter(Boolean);
            const combined = parts.join(' - ');
            set(
                localData,
                'meta_title',
                combined
                    ? (combined.length > 80 ? combined.substring(0, 80) : combined)
                    : null
            );
        }
    );

    watch(() => localData.entradas_compartidas, newVal => {
        if (newVal) {
            getEventos(localData.start_event_date);
        }
    });

    watch(() => localData.start_event_date, (newVal, oldVal) => {
        const newStart = newVal ? new Date(newVal).getTime() : null;
        const oldStart = oldVal ? new Date(oldVal).getTime() : null;

        if (newStart !== oldStart) {
            set(localData, 'registro_preventa_desde', null);
            set(localData, 'registro_preventa_hasta', null);
            set(localData, 'venta_preventa_desde', null);
            set(localData, 'venta_preventa_hasta', null);
        }
    });

    watch(() => localData.preventa_configurada, newVal => {
        if (newVal) {
            set(localData, 'venta_online_cerrada', true);
            notify('info', 'Venta online se ha cerrado automáticamente al activar la preventa.');
            if (!localData.texto_condiciones_legales_preventa) {
                set(localData, 'texto_condiciones_legales_preventa', textoCondicionesLegalesPreventaDefault);
            }
        } else {
            set(localData, 'venta_online_cerrada', false);
            notify('warning', 'Se ha desmarcado el check de venta online cerrada');
        }
    });

    watch(() => localData.recinto, newVal => {
        if (!newVal) {
            if (localData.permitir_liberacion_asientos) {
                set(localData, 'permitir_liberacion_asientos', false);
                notify('warning', 'Se ha desmarcado la liberación de asientos (requiere recinto)');
            }
        }
        syncCesionTemporalAsiento();
    });

    watch(() => localData.cerrar_butacas_abonados, () => {
        syncCesionTemporalAsiento();
    });

    watch(() => localData.descuento_abonado, (newVal, oldVal) => {
        // Capture cesion state BEFORE sync so we can detect "user turned
        // ON día del abonado while cesion was active" — mirror of the
        // toast shown the other way around (when user activates cesion
        // with día del abonado already on). Without this guard, cesion
        // gets disabled silently.
        const cesionWasOn = localData.permitir_cesion_temporal_asiento;
        syncCesionTemporalAsiento();
        if (newVal === true && oldVal === false && cesionWasOn) {
            notify('warning', `Se ha desmarcado la cesión temporal de asientos (${cesionTemporalBloqueadaPor.value}).`);
        }
    });

    watch(() => localData.permitir_cesion_temporal_asiento, newVal => {
        if (newVal) {
            syncCesionTemporalAsiento();
            // Only notify if sync actually disabled cesión — if no blocker
            // was active, sync is a no-op and there's nothing to warn about.
            // Without this guard we'd see a phantom "desmarcada (null)"
            // toast on every successful activation.
            if (!localData.permitir_cesion_temporal_asiento) {
                notify('warning', `Se ha desmarcado la cesión temporal de asientos (${cesionTemporalBloqueadaPor.value}).`);
            }
        }
    });

    // Cross-validation cliente (rango de abonado y acompañantes) la manejan
    // las custom rules de vee-validate declaradas en EventoAbonados.vue
    // (validateRangoDesde, validateRangoHasta, validateAcompañantesMin,
    // validateAcompañantesMax). vee-validate se encarga del error inline,
    // del atributo `data-validation-error=""` que usa scrollToFirstError,
    // y de bloquear el submit. No necesitamos watchers custom aquí.

    // Auto-limpieza para Tipos/Precios no permitidos (bloque amigo_abonado):
    // son all-or-nothing. Si el usuario vacía uno, limpiamos el otro para
    // que no quede un registro a medias (mitad tipo sin precio, o precio
    // sin tipo) que dispararía el error de la custom rule en submit.
    //
    // IMPORTANTE: cada watcher solo limpia el OTRO campo si este TIENE
    // valores. Si los dos están vacíos, ambos watchers ven length===0
    // pero el check length>0 del otro falla → no limpian → no se
    // dispara el otro watcher → se rompe el bucle infinito que
    // congelaba el navegador (caso reportado).
    watch(() => localData.amigos_tipos_abonos_ids, nuevosTipos => {
        if (!nuevosTipos || nuevosTipos.length === 0) {
            const descuentos = localData.amigos_descuentos_abonos_ids || [];
            if (descuentos.length > 0) {
                set(localData, 'amigos_descuentos_abonos_ids', []);
            }
        }
    });
    watch(() => localData.amigos_descuentos_abonos_ids, nuevosDescuentos => {
        if (!nuevosDescuentos || nuevosDescuentos.length === 0) {
            const tipos = localData.amigos_tipos_abonos_ids || [];
            if (tipos.length > 0) {
                set(localData, 'amigos_tipos_abonos_ids', []);
            }
        }
    });

    // Al activar "Experiencia", el evento deja de ser SGA/LFP: se limpian el
    // Evento SGA/LFP seleccionado, el producto asociado y las entradas
    // nominativas (dejan de ser obligatorias porque ya no hay evento
    // seleccionado). El payload del submit también re-limpia estos campos
    // como red de seguridad (useEventFormSubmit.crearEvento).
    watch(() => localData.experiencia, newVal => {
        if (newVal) {
            const teniaEvento = !!(localData.sga_id || localData.lfp_id);
            set(localData, 'sga_id', null);
            set(localData, 'sga_product_id', null);
            set(localData, 'lfp_id', null);
            set(localData, 'solicitar_datos', false);
            if (teniaEvento) {
                notify(
                    'info',
                    'Se han limpiado el Evento SGA/LFP y los productos asociados al activar "Experiencia".'
                );
            }
        }
    });

    // Si el usuario activa "Cerrar la venta online definitivamente" cuando
    watch(() => localData.venta_online_cerrada_definitiva, newVal => {
        if (newVal && localData.venta_online_cerrada) {
            set(localData, 'venta_online_cerrada', false);
            set(localData, 'cuenta_atras', null);
            notify(
                'warning',
                'Se ha desactivado "Cerrar la venta online" al cerrar la venta definitivamente.'
            );
        }
    });

    // Caso inverso al watcher anterior: si el usuario activa "Cerrar la
    // venta online" cuando "Cerrar la venta online definitivamente" ya
    // estaba marcada, desactivamos la definitiva y notificamos. Razón:
    // si el usuario quiere un cierre temporal con fecha de apertura, ya
    // no quiere un cierre permanente.
    //
    // NO hay loop infinito: cuando este watcher setea
    // `venta_online_cerrada_definitiva = false`, el watcher de arriba se
    // dispara (porque esa key cambió) pero la condición `if (newVal && ...)`
    // es false (newVal = false) y termina sin hacer nada.
    watch(() => localData.venta_online_cerrada, newVal => {
        if (newVal && localData.venta_online_cerrada_definitiva) {
            set(localData, 'venta_online_cerrada_definitiva', false);
            notify(
                'warning',
                'Se ha desactivado "Cerrar la venta online definitivamente" al activar "Cerrar la venta online".'
            );
        }
    });
}